import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";

import { Grid, Typography } from "@material-ui/core";
import { cloneDeep } from "lodash";

import Loader from "common/Loader";
import AppConstants from "../constants";
import GridWrapper from "../common/GridWrapper";

import { useStyle } from "./tip.styles";
import { useSelector } from "react-redux";
import { AppState } from "../config/redux/reducers";
import Filter from "../common/FilterForm";
import {
  fetchTipRecords,
  exportTipDataToExcel,
  resetExportInitiated,
} from "./redux/DriverHelperTipSlice";
import { getOrderStatusField } from "../utils/helpers.utils";
import {
  createFetchTipRecordsPayload,
  transformDataList,
} from "./response.transforms";
import { KeyConstants } from "constants/DriverHelperTipConstants";
import { TipInterface } from "./DriverHelperTipInterface";
import {
  Header,
  FormHeader,
  RenderCellParams,
  Filters,
  Chip,
  PayloadFilters,
} from "./DriverHelperTipInterface";
import useExportInitiated from "CustomHook/ExortInitiatedHook";
import ExportInitiatedDialog from "ExportIntiaiedDialog";
import { useUACHelper } from "../contexts/UACContext";

const DriverhelperTip = () => {
  const isFirstRender = useRef(true);
  const dispatch = useDispatch();
  const classes = useStyle();
  const onLoadRef = useRef(true);
  const uacHelper = useUACHelper();

  /**
   * Filters chips based on the UAC tabs granted for Driver Tips (DM_TIPS_ALL/DM_TIPS_EXPRESS/DM_TIPS_STANDARD).
   * @returns {Chip[]} - Array of chips the current user is permitted to see.
   */
  const filterChipsByRole = (): Chip[] => {
    return AppConstants.TIP_CONSTANTS.FILTER_CHIPS.filter((chip: Chip) =>
      uacHelper.hasAccess(chip.uacKey)
    );
  };

  const { countryCode, hubCode } = useSelector(
    (state: AppState) => state.common
  );


  const {
    loading,
    tipData,
    filterDropdowns,
    exportInitiated,
    reportId,
  } = useSelector((state: AppState) => state.driverHelperTip);
  const [activeOrdersFilterChip, setActiveOrdersFilterChip] = useState(() => {
    const chips = filterChipsByRole();
    return chips.length === 1 ? chips[0].code : AppConstants.TIP_CONSTANTS.DEFAULT_FILTER_CHIP;
  });

  const {
    openExportInitiatedInfo,
    handleExportInitiatedDialogClose,
  } = useExportInitiated(resetExportInitiated, exportInitiated);

  const [tipListToShow, setTipListToShow] = useState([] as TipInterface[]);
  const [appState, setAppState] = useState({
    userSearchValue: "",
    openSnackbar: false,
    snackbarType: AppConstants.SNACKBAR.TYPES.SUCCESS,
    snackbarMessage: "",
  });
  const [tableWrapperData, setTableWrapperData] = useState({
    ...AppConstants.TIP_CONSTANTS.TABLE_WRAPPER_DATA,
    HeaderData: AppConstants.TIP_CONSTANTS.TABLE_WRAPPER_DATA.HeaderData.map(
      (header: Header) => {
        const newHeader = { ...header };
        if (newHeader.field === KeyConstants.CONSIGNMENT_STATUS) {
          newHeader.renderCell = (params: RenderCellParams) =>
            params.value ? (
              <Grid
                className={classes.statusHeaderContainer}
                container
                alignItems="center"
              >
                <Typography
                  className={[
                    "tag",
                    getOrderStatusField("color", params.value),
                  ].join(" ")}
                >
                  {params.value ? params.value : ""}
                </Typography>
              </Grid>
            ) : (
              <></>
            );
        }
        return newHeader;
      }
    ),
    defaultFiltersObj: {
      ...AppConstants.TIP_CONSTANTS.TABLE_WRAPPER_DATA.defaultFiltersObj,
    },
  });
  const [activeFilters, setActiveFilters] = useState<Filters>({
    ...tableWrapperData.defaultFiltersObj,
  });

  const filterCallback = (
    filters: Filters,
    isLoadMore?: boolean,
    isChipFilterClicked?: boolean,
    selectedChip?: string
  ) => {
    if (filters) {
      const payloadFilters: PayloadFilters = {
        ...cloneDeep(filters),
        pageSize: tableWrapperData.loadMore.pageSize,
      };
      setActiveFilters(filters);
      payloadFilters.pageSize = tableWrapperData.loadMore.pageSize;
      const chipToUse = isChipFilterClicked
        ? selectedChip
        : activeOrdersFilterChip;

      const payload = createFetchTipRecordsPayload(
        payloadFilters,
        tipData,
        isLoadMore,
        countryCode,
        chipToUse
      );
      dispatch(fetchTipRecords({filters:payload}));
    }
  };

  const searchFilterHandler = (
    data: { [key: string]: string },
    updatedSearchValue: string,
    filters: Filters
  ) => {
    const activeFilters = cloneDeep(filters);
    onLoadRef.current = false;
    setAppState({ ...appState, userSearchValue: updatedSearchValue });
    if (updatedSearchValue) {
      for (const searchKey in data) {
        if (data[searchKey]) {
          activeFilters.searchAttribute = {
            key: searchKey,
            value: data[searchKey],
          };
          filterCallback(activeFilters);
          return;
        }
      }
    }
  };

  const handleLoadMoreDisableState = () => {
    const loadMoreState = tableWrapperData.loadMore;
    loadMoreState.disabled = tipData.totalPages
      ? tipData.pageNumber + 1 >= tipData.totalPages
      : true;
    setTableWrapperData({ ...tableWrapperData, loadMore: loadMoreState });
  };

  const configureBasedOnCountryCode = () => {
    if (isFirstRender.current) {
      isFirstRender.current = false; // Set to false after the first render
      return; // Exit the useEffect early
    }
    if (countryCode) {
      filterCallback(activeFilters);
    }
  };


/**
 * Updates the filters and table wrapper data when a specific filter is changed.
 * @param name - The name of the filter being updated.
 * @param value - The new value for the filter.
 */
  const fetchCallbackOnOrder = (name: string, value: string) => {
    const updatedFilters: Filters = {
      ...activeFilters,
      [name]: { ...activeFilters[name as keyof Filters], value, key: name },
    };
  
    setTableWrapperData((prev) => ({
      ...prev, // Preserve the existing tableWrapperData state
      defaultFiltersObj: {
        ...prev.defaultFiltersObj, // Preserve the existing default filters
        [name]: {
          ...(prev.defaultFiltersObj as Filters)[name as keyof Filters], // Preserve the existing filter properties
          value, // Update the filter's value with the new value
          key: name, // Ensure the key is updated to match the filter name
        },
      },
    }));
  
    filterCallback(updatedFilters);
  };

  useEffect(() => {
    if (tableWrapperData?.HeaderData || tableWrapperData?.FilterFormData) {
      const headerData = tableWrapperData?.HeaderData?.map((header: Header) => {
        if (header.filterObj) {
          if (header.filterObj.fieldName === KeyConstants.ASSIGNEE_VENDOR) {
            header.filterObj.items = filterDropdowns.assigneeVendor || [];
          }
          if (header.filterObj.fieldName === KeyConstants.CONSIGNMENT_STATUS) {
            header.filterObj.items = filterDropdowns.consignmentStatus || [];
          }
        }
        return header;
      });
      const formData = tableWrapperData?.FilterFormData?.map(
        (formHeader: any) => {
          if (formHeader) {
            if (formHeader.fieldName === KeyConstants.ASSIGNEE_TYPE) {
              formHeader.items = AppConstants.TIP_CONSTANTS.assigneeTypeDropDown;
            }
          }
          return formHeader;
        }
      );

      setTableWrapperData({
        ...tableWrapperData,
        FilterFormData: formData,
        HeaderData: headerData,
      });
    }
  }, [filterDropdowns]);

  useEffect(() => {
    const hubsToAdd = hubCode.map((hub: string) => ({
      name: hub,
      value: hub,
    }));
    const updatedFilters: Filters = {
      ...activeFilters,
      hubCode: { ...activeFilters.hubCode, value: hubsToAdd },
    };
    if (!onLoadRef.current) {
      filterCallback(updatedFilters);
    }
    onLoadRef.current = false;
  }, [hubCode]);

  useEffect(() => {
    configureBasedOnCountryCode();
  }, [countryCode]);

  /**
   * Handles the logic for filtering records based on the selected chip .
   * @param chip - The selected chip object.
   */

  const handleChipFilter = useCallback(
    (chip: Chip) => {
      onLoadRef.current = false;
      if (chip.code !== activeOrdersFilterChip) {
        const filters = { ...activeFilters, defaultFiltersObj: {
          ...AppConstants.TIP_CONSTANTS.TABLE_WRAPPER_DATA.defaultFiltersObj,
        } }; 
        filters.assigneeType = {
          ...filters.assigneeType,
          value: AppConstants.SELECT_ALL.value, // Reset to the initial value (empty string)
        }; 
        setTableWrapperData({
          ...tableWrapperData, defaultFiltersObj: {
            ...AppConstants.TIP_CONSTANTS.TABLE_WRAPPER_DATA.defaultFiltersObj,
          }})
        setActiveOrdersFilterChip(chip.code);
        filterCallback(filters, false, true, chip.code);
      
      }
    },
    [activeOrdersFilterChip, activeFilters]
  );
 
  const filteredChips = useMemo(() => filterChipsByRole(), [uacHelper]);

  useEffect(() => {
    if (filteredChips.length === 1 && filteredChips[0].code !== activeOrdersFilterChip) {
      setActiveOrdersFilterChip(filteredChips[0].code);
      filterCallback(activeFilters, false, true, filteredChips[0].code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredChips]);

  const getFilterChips = () => {
    const filterChips = filterChipsByRole();

    return filterChips.map((chip: Chip, index: number) => (
      <Grid
        key={`chipFilterItem-${index}`}
        item
        className={`chipFilterItem ${index === 0 ? "firstItem" : ""} ${
          activeOrdersFilterChip === chip.code ? "active" : ""
        }`}
        onClick={() => handleChipFilter(chip)}
      >
        <Grid container className="chipContainer">
          <Grid item className="chipItem name">
            <Typography>{chip.value}</Typography>
          </Grid>
        </Grid>
      </Grid>
    ));
  };

  const renderDataForSearchSection = () => {
    return (
      <Grid container className={classes.searchSectionContainer}>
        {AppConstants.TIP_CONSTANTS.TABLE_WRAPPER_DATA.FilterFormData.length
          ? AppConstants.TIP_CONSTANTS.TABLE_WRAPPER_DATA.FilterFormData.map(
              (filter: FormHeader) => (
                <Filter
                  key={filter.key}
                  filter={filter}
                  defaultValues={tableWrapperData.defaultFiltersObj}
                  fetchDataCallback={fetchCallbackOnOrder}
                />
              )
            )
          : null}
      </Grid>
    );
  };

  const handleDownloadUserDataDialogOpen = () => {
    const payloadFilters = cloneDeep(activeFilters);
    const payload = createFetchTipRecordsPayload(
      payloadFilters,
      tipData,
      false,
      countryCode,
      activeOrdersFilterChip
    );
    dispatch(exportTipDataToExcel({payload: payload }));
  };

  const handleHeaderButtonClick = (btnObj: any) => {
    if (btnObj && btnObj.type) {
      switch (btnObj.type) {
        case AppConstants.BUTTONS.DOWNLOAD_ICON:
          handleDownloadUserDataDialogOpen();
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
   
      setTipListToShow(transformDataList(tipData) as TipInterface[]);
      handleLoadMoreDisableState();
 
  }, [tipData]);

  return (
    <div className={classes.root}>
      {loading && <Loader></Loader>}
      <Grid className={classes.container} container>
        <Grid className="userItem content" item>
          <Grid item className="panelItem chipFilter">
            <Grid container className="chipsContainer">
              {getFilterChips()}
            </Grid>
          </Grid>
          <GridWrapper
            showHeader={false}
            checkboxSelection={false}
            headerData={tableWrapperData.HeaderData}
            rowData={tipListToShow}
            activeFilter={activeFilters}
            loading={loading}
            searchTypes={tableWrapperData.SEARCH_TYPES}
            defaultFilter={tableWrapperData.defaultFiltersObj}
            title={tableWrapperData.title}
            disableSelectionOnClick={true}
            searchValue={appState.userSearchValue}
            loadMore={{
              ...tableWrapperData.loadMore,
              rowCount: tipData.totalElements || 0,
            }}
            searchCallback={searchFilterHandler}
            filterGrid={filterCallback}
            showDataNextToSearchButton={true}
            renderDataForSearchSection={renderDataForSearchSection}
            headerButtons={tableWrapperData.headerButtons}
            headerBtnClick={handleHeaderButtonClick}
          />
        </Grid>
      </Grid>
      {openExportInitiatedInfo && (
        <ExportInitiatedDialog
          openExportInitiatedInfo={openExportInitiatedInfo}
          handleDialogClose={handleExportInitiatedDialogClose}
          reportId={reportId}
        />
      )}
    </div>
  );
};

export default React.memo(DriverhelperTip);
